import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { describeChannels } from './iotChannels';

const STATUS_LABEL = {
  PENDING: { label: '대기', color: '#4AB3BC' },
  COMPLETED: { label: '완료', color: '#4CAF50' },
  FAILED: { label: '실패', color: '#E53935' },
  CANCELED: { label: '취소', color: '#B3B3B3' },
  EXPIRED: { label: '만료', color: '#B3B3B3' },
};
const DAY_LABEL = ['일', '월', '화', '수', '목', '금', '토'];

// "2026-09-17 09:00:00" (또는 T 구분) → "09-17 (수) 09:00"
const formatExecuteAt = value => {
  if (!value) return '';
  const [date, time = ''] = String(value).split(/[T ]/);
  const [y, m, d] = date.split('-').map(Number);
  return `${date.slice(5)} (${DAY_LABEL[new Date(y, m - 1, d).getDay()]}) ${time.slice(0, 5)}`;
};

const formatDuration = minutes => {
  const total = Number(minutes) || 0;
  const h = Math.floor(total / 60);
  const rest = total % 60;
  if (h && rest) return `${h}시간 ${rest}분`;
  if (h) return `${h}시간`;
  return `${rest}분`;
};

// 예약 등록 모달과 같은 말로. 예: "조명 1,2 켜짐 · 출입문 개방 · 30분 유지 후 전원 꺼짐·자동잠김"
const describeReservation = (r, relays) => {
  const parts = [];
  const onOff = r.onOff === 'off' ? '꺼짐' : '켜짐';
  if (r.controlType === 'LIGHT_ALL') parts.push(`전원 전체 ${onOff}`);
  else if (r.controlType === 'LIGHT') parts.push(`${describeChannels(r.relayChannels, relays) || '전원'} ${onOff}`);
  if (r.periodTime || r.doorOpenYn === 'Y') parts.push('출입문 개방');
  const summary = parts.join(' · ') || '-';
  return r.periodTime ? `${summary} · ${formatDuration(r.periodTime)} 유지 후 전원 꺼짐·자동잠김` : summary;
};

const List = styled(Box)({ display: 'flex', flexDirection: 'column' });

const Item = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '12px 4px',
  borderTop: '1px solid #F0F0F0',
});

const Info = styled(Box)({ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, flex: 1 });
const TopRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' });
const When = styled(Typography)({ fontSize: '14px', fontWeight: 600, color: '#333', whiteSpace: 'nowrap' });
const Badge = styled(Box, { shouldForwardProp: p => p !== 'color' })(({ color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 8px',
  border: `1px solid ${color}`,
  borderRadius: '999px',
  color,
  fontSize: '11px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
}));
const Desc = styled(Typography)({ fontSize: '14px', color: '#4D4D4D' });
const Name = styled(Typography)({ fontSize: '12px', color: '#999' });
const Fail = styled(Typography)({ fontSize: '12px', color: '#E53935' });
const Actions = styled(Box)({ display: 'flex', gap: '6px', flexShrink: 0 });
const SmallButton = styled(Button, { shouldForwardProp: p => p !== 'danger' })(({ danger }) => ({
  minWidth: 0,
  height: '30px',
  padding: '0 10px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  color: danger ? '#D32F2F' : '#0F7F85',
  border: `1px solid ${danger ? '#E8A0A0' : '#BFE3E6'}`,
  backgroundColor: '#fff',
}));
const Message = styled(Typography)({ fontSize: '14px', color: '#999', textAlign: 'center', padding: '16px 0 4px' });

/**
 * 예약내역 인라인 목록 (선택 룸 센서 기준). 수정·취소는 대기(PENDING)만 — 서버가 그 외 상태는 400 으로 거부
 */
const ReservationHistoryList = ({ relays = [], reservations = [], isLoading = false, cancelingId, onEdit, onCancel }) => {
  const { t } = useTranslation();

  if (isLoading) return <Message>{t('예약 내역을 불러오는 중...')}</Message>;
  if (reservations.length === 0) return <Message>{t('등록된 예약이 없습니다.')}</Message>;

  return (
    <List>
      {reservations.map(item => {
        const status = STATUS_LABEL[item.status] || { label: item.status, color: '#666' };
        return (
          <Item key={item.id}>
            <Info>
              <TopRow>
                <When>{formatExecuteAt(item.executeAt)}</When>
                <Badge color={status.color}>{t(status.label)}</Badge>
              </TopRow>
              <Desc>{describeReservation(item, relays)}</Desc>
              {item.reservationName && <Name>{item.reservationName}</Name>}
              {item.status === 'FAILED' && item.failReason && <Fail>{item.failReason}</Fail>}
            </Info>
            {item.status === 'PENDING' && (
              <Actions>
                <SmallButton onClick={() => onEdit?.(item)}>{t('수정')}</SmallButton>
                <SmallButton danger disabled={cancelingId === item.id} onClick={() => onCancel?.(item)}>
                  {t('취소')}
                </SmallButton>
              </Actions>
            )}
          </Item>
        );
      })}
    </List>
  );
};

export default ReservationHistoryList;
